"use client";

import * as React from "react";

import { captureNotificationFocus } from "~/components/ui/focus-context";
import { useToast } from "~/components/ui/toast";
import { mutationErrorMessage } from "~/lib/errors/mutation-error";
import { track as capture } from "~/lib/analytics/client";
import type { CartLineView } from "~/lib/cart/summary";
import { useCart } from "~/lib/cart/use-cart";

/** Shared P4-DB01 removal flow: remove immediately, then keep a once-only restore available until dismissed. */
export function useUndoableCartRemove() {
  const { addItem, removeItem } = useCart();
  const { toast } = useToast();

  return React.useCallback(
    async (line: CartLineView) => {
      const returnFocus = captureNotificationFocus();
      await removeItem(line.id);
      let restored = false;
      // P9-01 · Funnel remove-from-cart signal.
      capture("REMOVE_FROM_CART", { variantId: line.variantId, quantity: line.quantity });
      toast({
        title: `${line.title} removed`,
        tone: "success",
        actionLabel: "Undo",
        returnFocus,
        onAction: async () => {
          if (restored) return;
          restored = true;
          await addItem({
            variantId: line.variantId,
            quantity: line.quantity,
            personalisation: line.personalisation,
          }).catch((error: unknown) => {
            toast({
              title: mutationErrorMessage(error, "Couldn't restore that item"),
              tone: "danger",
            });
          });
        },
      });
    },
    [addItem, removeItem, toast],
  );
}
